import React from 'react';
import { observer } from 'mobx-react';
import Table, { ColumnProps } from 'antd/lib/table';
import { Spin } from 'antd';
import { useRppAdStore } from '../../contexts/mobx';
import { useNavigation } from '../../utils/use-navigation';
import {
    normalizeRppAdSearchParams,
    RppAd,
    RppAdSearchParams,
    RppAdStatus,
} from '../../stores/rpp-ad';
import { CreateButton } from '../../components/action-buttons';
import { PageTopBar } from '../../components/page-top-bar';
import { PaginationBar } from '../../components/pagination/pagination-bar';
import { Tag } from '../../components/tag';
import type { TagVariant } from '../../components/tag';
import { useSearchParams } from '../../utils/use-search-params';

function statusVariant(status: RppAdStatus): TagVariant {
    switch (status) {
        case 'active':
            return 'success';
        case 'archived':
            return 'grey';
        default:
            return 'gold';
    }
}

export const RppAdsPage = observer(() => {
    const store = useRppAdStore();
    const navigation = useNavigation();
    const search = useSearchParams<RppAdSearchParams>();
    const normalizedSearchParams = normalizeRppAdSearchParams(search.params);

    React.useEffect(() => {
        store.fetchRppAdList(search.params);
    }, [search.params, store]);

    const columns = React.useMemo<ColumnProps<RppAd>[]>(
        () => [
            {
                title: 'Id',
                dataIndex: 'id',
                key: 'id',
                width: '5rem',
                ellipsis: true,
            },
            {
                title: 'Title',
                dataIndex: 'title',
                key: 'title',
                ellipsis: true,
            },
            {
                title: 'Advertiser',
                key: 'advertiser',
                ellipsis: true,
                render: (_: unknown, record: RppAd) => record.advertiser ?? '—',
            },
            {
                title: 'Status',
                key: 'status',
                width: '8rem',
                render: (_: unknown, record: RppAd) => (
                    <Tag variant={statusVariant(record.status)}>
                        {record.status}
                    </Tag>
                ),
            },
            {
                title: 'Dates',
                key: 'dates',
                ellipsis: true,
                render: (_: unknown, record: RppAd) =>
                    `${record.startDate} → ${record.endDate}`,
            },
            {
                title: 'Provinces',
                key: 'provinces',
                width: '8rem',
                render: (_: unknown, record: RppAd) =>
                    record.provinceIds.length,
            },
        ],
        []
    );

    return (
        <>
            <PageTopBar title="RPP Ads">
                <CreateButton onClick={() => navigation.goToRppAdCreate()} />
            </PageTopBar>

            <PaginationBar
                pagination={{
                    page: store.rppAds
                        ? store.rppAds.page - 1
                        : normalizedSearchParams.page,
                    pageSize:
                        store.rppAds?.perPage ?? normalizedSearchParams.perPage,
                    total: store.rppAds?.total,
                    onPageChange: (page) => search.setParams({ page }),
                    onPageSizeChange: (perPage) =>
                        search.setParams({ perPage, page: 0 }),
                }}
            />

            {store.isFetchingList && !store.rppAds ? (
                <Spin />
            ) : (
                <Table<RppAd>
                    className="clickable_table_body_rows"
                    rowKey="id"
                    dataSource={store.rppAds?.results}
                    columns={columns}
                    loading={store.isFetchingList}
                    pagination={false}
                    bordered
                    onRow={(record) => ({
                        onClick: () => navigation.goToRppAdEdit(record.id),
                    })}
                />
            )}
        </>
    );
});
